import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { AppType, Theme } from 'webapp/enums/enums';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { deleteKeys } from 'webapp/utils/common';

import Serviced from 'webapp/models-custom/serviced';
import type { BootData } from 'webapp/data-loader/base';
import type DataLoaderBase from 'webapp/data-loader/base';

export default class ApplicationRoute extends Route {
	@service declare router: Service.Router;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('request') declare requestService: Service.Request;
	@service('tour') declare tourService: Service.Tour;
	@service('version-checker') declare versionCheckerService: Service.VersionChecker;
	@service('constants') declare constantsService: Service.Constants;
	@service('logger') declare loggerService: Service.Logger;
	@service('socket') declare socketService: Service.Socket;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('leave-guard') declare leaveGuardService: Service.LeaveGuard;
	@service('genie-thread-agency-return')
	declare genieThreadAgencyReturnService: Service.GenieThreadAgencyReturn;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('connect') declare connectService: Service.Connect;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('request-queue') declare requestQueueService: Service.RequestQueue;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('timer') declare timerService: Service.Timer;

	override queryParams = {
		anchor: {
			replace: true,
		},
	};

	// for some reason sometimes the hooks get called twice each
	// these flags are used to trigger their logic only once
	private beforeModelCalled = false;
	private afterModelCalled = false;

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);

		// set the container on the created models (to use @service etc)
		Serviced.appInstance = getOwner(this)!;

		// initialize globals for some jquery stuff
		$.ajaxSetup({
			timeout: 30000,
		});

		$.extend($.lazyLoadXT, {
			autoInit: false,
		});

		// initializing certain services
		this.navigationService.appRoute = this;
		// Listens on the EmberRouter, which triggers ahead of every router-service listener (the
		// navigation ones, the activatedRoute one below), so they all see a held route change as
		// aborted and skip it instead of closing the Genie side panel and losing its work.
		this.leaveGuardService.bindRouterEvents();
		this.navigationService.bindRouterEvents();
		this.loggerService.bindGLobalErrorHandlers();
		this.versionCheckerService.bindEvents();
		this.timerService.increaseTicker();

		// No coredata means the app was loaded via the E2E shell (dist/e2e.html),
		// where seeding happens later through the e2e-seed service.
		const globals: BootData = window.coredata || { appType: AppType.E2E };
		window.coredata = undefined;

		window.env = globals.env!; // undefined in the E2E shell
		deleteKeys(globals, 'env');

		this.navigationService.appType = globals.appType;
		deleteKeys(globals, 'appType');

		// initialize the theme
		const themeId = (globals.themeId ||
			globals.userSettingsValues?.[UserSettingsKey.COLOR_MODE] ||
			Theme.LIGHT) as Theme;
		this.navigationService.createTheme(themeId);
		deleteKeys(globals, 'themeId');

		const dataLoader: DataLoaderBase = this.navigationService.initializeDataLoader();
		dataLoader.loadGlobals(globals);

		this.router.on('routeWillChange', (transition: PublicTransition) => {
			// marks next active route, already before transition happens
			// used in navigation-main and navigation-section
			// ignore loading subroutes!
			if (!String(transition.to?.name).includes('loading') && !transition.isAborted) {
				this.navigationService.activatedRoute = transition.to?.name || '';
				this.navigationService.activatedRouteParams = transition.to?.params ?? {};
				this.navigationService.validateAppTransition(transition);
			}
		});

		this.accountService.registerNumeralLocales();
	}

	override beforeModel() {
		if (!this.beforeModelCalled) {
			this.beforeModelCalled = true;
			this.genieThreadAgencyReturnService.captureFromUrl();
			return this.navigationService.dataLoader.doInitialRequests();
		}
		return undefined;
	}

	override afterModel(_model: unknown, transition: PublicTransition) {
		if (!this.afterModelCalled) {
			this.afterModelCalled = true;
			return this.navigationService.dataLoader.initialRequestsPromise.then(() => {
				return this.navigationService.dataLoader.afterInitialRequests(
					transition.to?.name || '',
				);
			});
		}
		return undefined;
	}

	@action
	override error(error: Error, transition: PublicTransition): void {
		// eslint-disable-next-line no-console
		console.log(transition);
		// eslint-disable-next-line no-console
		console.error(error);
		this.loggerService.logError('Application error', {
			error,
			transition,
		});
		if (error?.message) {
			this.infolayerService.error(error.message);
		}
		this.navigationService.transitionToRoute('index');
	}
}
